import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Inventory2 as InventoryIcon,
  Check as CheckIcon,
  AddPhotoAlternate as AddPhotoIcon,
  DeleteOutline as DeleteOutlineIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { Inventory, InventoryFormData, InventoryImageFormData, InventoryImage } from '../types';
import InventoryService from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/apiConfig';
import { fetchWithAuth } from '../utils/authSession';
import { normalizeImageUrl } from '../utils/imageUtils';
import { chaaviGoldAddImageButtonSx } from './ChaaviVendorDashboardShell';

const IM = {
  ink: '#1a1a1a',
  sub: '#888888',
  label: '#999999',
  required: '#e24b4a',
  border: '#dddddd',
  borderSoft: '#e4e4e4',
  rule: '#f0f0f0',
  fieldBg: '#fafafa',
  green: '#4a7c59',
  greenHover: '#3d6b4b',
  greenMuted: '#f5f9f6',
  greenTint: '#f0f7f0',
  greenFocusRing: 'rgba(74,124,89,0.1)',
  hint: '#aaaaaa',
  cancelColor: '#666666',
} as const;

const inputPropsSx = {
  fontSize: 14,
  color: IM.ink,
  '&::placeholder': { color: '#aaa', opacity: 1 },
};

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: IM.fieldBg,
    minHeight: 40,
    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    '& fieldset': { borderWidth: '0.5px', borderColor: IM.border },
    '&:hover fieldset': { borderColor: '#cccccc' },
    '&.Mui-focused': {
      bgcolor: '#ffffff',
      boxShadow: `0 0 0 3px ${IM.greenFocusRing}`,
    },
    '&.Mui-focused fieldset': { borderWidth: '0.5px', borderColor: IM.green },
    '&.Mui-error fieldset': { borderColor: IM.required },
  },
  '& .MuiOutlinedInput-input': { py: '10px', px: '12px', height: 'auto' },
  '& .MuiOutlinedInput-inputMultiline': { py: '10px', minHeight: 88, lineHeight: 1.5 },
};

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: IM.label,
        display: 'block',
        mb: '5px',
        '& .req': { color: IM.required, ml: '2px' },
      }}
    >
      {children}
      {required ? <Box component="span" className="req">*</Box> : null}
    </Typography>
  );
}

function SectionRule() {
  return <Box sx={{ height: '0.5px', bgcolor: IM.rule, my: 0.5 }} />;
}

interface InventoryManagementFormProps {
  open: boolean;
  onClose: () => void;
  inventory: Inventory | null;
  businessId: string;
  onSuccess: () => void;
}

type StagedUpload = { file: File; preview: string };

const FORM_ID = 'inventory-management-form';

const InventoryManagementForm: React.FC<InventoryManagementFormProps> = ({
  open,
  onClose,
  inventory,
  businessId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<InventoryFormData>({
    businessId: businessId,
    inventoryName: '',
    inventoryDescription: '',
    inventoryCategory: '',
    price: 0,
    quantity: 0,
  });
  const [priceInput, setPriceInput] = useState<string>('');
  const [quantityInput, setQuantityInput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stagedUploads, setStagedUploads] = useState<StagedUpload[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [existingImages, setExistingImages] = useState<InventoryImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<InventoryImage | null>(null);
  const [customCategory, setCustomCategory] = useState<string>('');
  const [showCustomCategory, setShowCustomCategory] = useState<boolean>(false);
  const { user } = useAuth();

  const inventoryCategories = React.useMemo(
    () => ['chair', 'table', 'plates', 'decoration', 'lighting', 'sound', 'catering', 'Other'],
    []
  );

  const revokeStagedPreviews = useCallback((items: StagedUpload[]) => {
    items.forEach((s) => URL.revokeObjectURL(s.preview));
  }, []);

  const fetchExistingImages = useCallback(async (inventoryId: string) => {
    try {
      setImagesLoading(true);
      const list = await InventoryService.getInventoryImagesByInventoryId(inventoryId);
      setExistingImages(list);
    } catch (e) {
      console.error(e);
      setExistingImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    if (inventory) {
      setFormData({
        businessId: inventory.businessId,
        inventoryName: inventory.inventoryName,
        inventoryDescription: inventory.inventoryDescription,
        inventoryCategory: inventory.inventoryCategory,
        price: inventory.price,
        quantity: inventory.quantity,
      });
      setPriceInput(inventory.price.toString());
      setQuantityInput(inventory.quantity.toString());

      if (!inventoryCategories.includes(inventory.inventoryCategory)) {
        setCustomCategory(inventory.inventoryCategory);
        setShowCustomCategory(true);
      } else {
        setCustomCategory('');
        setShowCustomCategory(false);
      }
      void fetchExistingImages(inventory.inventoryId);
    } else {
      setFormData({
        businessId: businessId,
        inventoryName: '',
        inventoryDescription: '',
        inventoryCategory: '',
        price: 0,
        quantity: 0,
      });
      setPriceInput('');
      setQuantityInput('');
      setCustomCategory('');
      setShowCustomCategory(false);
      setExistingImages([]);
    }
    setError(null);
  }, [inventory, businessId, open, inventoryCategories, fetchExistingImages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'price') {
      setPriceInput(value);
      setFormData((prev) => ({
        ...prev,
        price: parseFloat(value) || 0,
      }));
    } else if (name === 'quantity') {
      setQuantityInput(value);
      setFormData((prev) => ({
        ...prev,
        quantity: parseInt(value, 10) || 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'inventoryCategory') {
      if (value === 'Other') {
        setShowCustomCategory(true);
        setFormData((prev) => ({
          ...prev,
          inventoryCategory: '',
        }));
      } else {
        setShowCustomCategory(false);
        setCustomCategory('');
        setFormData((prev) => ({
          ...prev,
          inventoryCategory: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCustomCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomCategory(value);
    setFormData((prev) => ({
      ...prev,
      inventoryCategory: value,
    }));
  };

  const handleAddStagedFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files || []);
    const valid: File[] = [];
    for (const file of picked) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) {
        setError('Each image must be 5MB or smaller.');
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0 && picked.length > 0) {
      setError('Only image files up to 5MB are allowed.');
    }
    if (valid.length > 0) {
      setError(null);
      setStagedUploads((prev) => [...prev, ...valid.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    }
    event.target.value = '';
  };

  const removeStagedAt = (index: number) => {
    setStagedUploads((prev) => {
      const item = prev[index];
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFilesToInventory = async (inventoryId: string, files: File[], currentExisting: InventoryImage[]) => {
    const needsNewPrimary = !currentExisting.some((i) => i.isPrimary);
    const token = localStorage.getItem('token');

    const uploadPromises = files.map(async (file, index) => {
      const isPrimary = needsNewPrimary && index === 0;

      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'inventory');
      fd.append('itemId', inventoryId);

      const uploadResponse = await fetchWithAuth(`${API_BASE_URL}/api/files/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Vendor-Phone': user?.phoneNumber || '',
        },
        body: fd,
      });

      let uploadResult: any;
      try {
        uploadResult = await uploadResponse.json();
      } catch {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      if (!uploadResponse.ok || uploadResult.success === false) {
        throw new Error(uploadResult.message || `File upload failed: ${uploadResponse.status}`);
      }

      const rawPath = uploadResult.filePath;
      const imageUrl = typeof rawPath === 'string' && rawPath.startsWith('http') ? rawPath : `${API_BASE_URL}${rawPath}`;

      const imageData: InventoryImageFormData = {
        inventoryId,
        imageName: file.name,
        imageUrl,
        imagePath: rawPath,
        imageSize: file.size,
        imageType: file.type,
        isPrimary,
      };

      return InventoryService.createInventoryImage(imageData, user?.phoneNumber);
    });

    await Promise.all(uploadPromises);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.inventoryName || !formData.inventoryDescription || !formData.inventoryCategory) {
      setError('Please fill in all required fields');
      return;
    }

    if (showCustomCategory && !customCategory.trim()) {
      setError('Please enter a custom category');
      return;
    }

    if (formData.price <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    if (formData.quantity < 0) {
      setError('Quantity cannot be negative');
      return;
    }

    const stagedFiles = stagedUploads.map((s) => s.file);
    const totalImagesAfter = existingImages.length + stagedFiles.length;

    if (!inventory && stagedFiles.length === 0) {
      setError('Please add at least one image for the inventory item');
      return;
    }

    if (inventory && totalImagesAfter === 0) {
      setError('An item must have at least one image. Add images or undo removals.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let targetInventoryId: string;

      if (inventory) {
        await InventoryService.updateInventory(inventory.inventoryId, formData, user?.phoneNumber);
        targetInventoryId = inventory.inventoryId;
      } else {
        const newInventory = await InventoryService.createInventory(formData, user?.phoneNumber);
        targetInventoryId = newInventory.inventoryId;
      }

      if (stagedFiles.length > 0) {
        setImageUploading(true);
        try {
          const existingForPrimary =
            inventory != null
              ? existingImages
              : await InventoryService.getInventoryImagesByInventoryId(targetInventoryId);
          await uploadFilesToInventory(targetInventoryId, stagedFiles, existingForPrimary);
        } catch (imageErr: any) {
          console.error(imageErr);
          setError(`Inventory saved, but images failed: ${imageErr.message || 'Unknown error'}`);
          setImageUploading(false);
          setLoading(false);
          setStagedUploads((prev) => {
            revokeStagedPreviews(prev);
            return [];
          });
          onSuccess();
          setExistingImages([]);
          setImageToDelete(null);
          onClose();
          return;
        } finally {
          setImageUploading(false);
        }
      }

      setStagedUploads((prev) => {
        revokeStagedPreviews(prev);
        return [];
      });
      onSuccess();
      setPriceInput('');
      setQuantityInput('');
      setCustomCategory('');
      setShowCustomCategory(false);
      setError(null);
      setExistingImages([]);
      setImageToDelete(null);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteImage = async () => {
    if (!imageToDelete || !user?.phoneNumber) return;
    try {
      await InventoryService.deleteInventoryImage(imageToDelete.imageId, user.phoneNumber);
      setExistingImages((prev) => prev.filter((i) => i.imageId !== imageToDelete.imageId));
      setImageToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    }
  };

  const handleSetPrimary = async (imageId: string) => {
    try {
      await InventoryService.setPrimaryInventoryImage(imageId, user?.phoneNumber);
      setExistingImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.imageId === imageId })));
    } catch (err: any) {
      setError(err.message || 'Failed to set primary image');
    }
  };

  const handleClose = () => {
    setStagedUploads((prev) => {
      revokeStagedPreviews(prev);
      return [];
    });
    setImageUploading(false);
    setPriceInput('');
    setQuantityInput('');
    setCustomCategory('');
    setShowCustomCategory(false);
    setError(null);
    setExistingImages([]);
    setImageToDelete(null);
    onClose();
  };

  const isEdit = Boolean(inventory);
  const stagedFiles = stagedUploads.map((s) => s.file);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.5)' } }}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 720,
            borderRadius: '16px',
            border: `0.5px solid ${IM.borderSoft}`,
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: '8px' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ px: 3.5, pt: 3, pb: 0, borderBottom: `0.5px solid ${IM.rule}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: IM.greenTint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.1,
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 20, color: IM.green }} />
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 500, color: IM.ink, m: 0, mb: '3px' }}>
                  {isEdit ? 'Edit inventory item' : 'Add inventory item'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: IM.sub, m: 0, mb: 2.5 }}>
                  {isEdit
                    ? 'Update details and photos for this listing.'
                    : 'Create an item clients can browse and book with your business.'}
                </Typography>
              </Box>
              {isEdit && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: '#eaf3ec',
                    color: '#3d6b4b',
                    fontSize: 11,
                    fontWeight: 500,
                    px: 1,
                    py: 0.375,
                    borderRadius: '20px',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: IM.green }} />
                  Active
                </Box>
              )}
            </Box>
          </Box>

          <Box
            component="form"
            id={FORM_ID}
            onSubmit={handleSubmit}
            sx={{ px: 3.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box>
              <FieldLabel required>Name</FieldLabel>
              <TextField
                required
                fullWidth
                name="inventoryName"
                value={formData.inventoryName}
                onChange={handleInputChange}
                placeholder="e.g. Wooden dining chair"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>

            <Box>
              <FieldLabel required>Description</FieldLabel>
              <TextField
                required
                fullWidth
                multiline
                rows={3}
                name="inventoryDescription"
                value={formData.inventoryDescription}
                onChange={handleInputChange}
                placeholder="Describe the item, condition, dimensions…"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: showCustomCategory ? '1fr 1fr' : '1fr' },
                gap: 1.5,
              }}
            >
              <Box>
                <FieldLabel required>Category</FieldLabel>
                <FormControl fullWidth required variant="outlined">
                  <Select
                    name="inventoryCategory"
                    value={showCustomCategory ? 'Other' : formData.inventoryCategory}
                    onChange={handleSelectChange}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Category' }}
                    renderValue={(v) => {
                      if (v === 'Other') return 'Other';
                      return v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : '';
                    }}
                    sx={{
                      borderRadius: '8px',
                      bgcolor: IM.fieldBg,
                      minHeight: 40,
                      fontSize: 14,
                      '& .MuiOutlinedInput-notchedOutline': { borderWidth: '0.5px', borderColor: IM.border },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cccccc' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '0.5px', borderColor: IM.green },
                      '&.Mui-focused': { bgcolor: '#fff', boxShadow: `0 0 0 3px ${IM.greenFocusRing}` },
                    }}
                  >
                    {inventoryCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {showCustomCategory && (
                <Box>
                  <FieldLabel required>Custom category</FieldLabel>
                  <TextField
                    required
                    fullWidth
                    name="customCategory"
                    value={customCategory}
                    onChange={handleCustomCategoryChange}
                    placeholder="Your category"
                    hiddenLabel
                    variant="outlined"
                    InputProps={{ sx: inputPropsSx }}
                    sx={outlinedFieldSx}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <Box>
                <FieldLabel required>Price (₹)</FieldLabel>
                <TextField
                  required
                  fullWidth
                  name="price"
                  type="number"
                  value={priceInput}
                  onChange={handleInputChange}
                  placeholder="0"
                  hiddenLabel
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ sx: inputPropsSx }}
                  sx={outlinedFieldSx}
                />
              </Box>
              <Box>
                <FieldLabel required>Quantity</FieldLabel>
                <TextField
                  required
                  fullWidth
                  name="quantity"
                  type="number"
                  value={quantityInput}
                  onChange={handleInputChange}
                  placeholder="0"
                  hiddenLabel
                  variant="outlined"
                  inputProps={{ min: 0 }}
                  InputProps={{ sx: inputPropsSx }}
                  sx={outlinedFieldSx}
                />
                <Typography sx={{ fontSize: 11, color: IM.hint, mt: '1px' }}>Units in stock</Typography>
              </Box>
            </Box>

            <SectionRule />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <FieldLabel required={!isEdit}>Images</FieldLabel>
                  <Typography sx={{ fontSize: 12, color: IM.sub, mt: 0.25 }}>
                    {!isEdit ? 'At least one image required.' : 'Add, remove, or choose a primary photo.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<AddPhotoIcon sx={{ fontSize: 18, color: '#fff' }} />}
                  sx={{
                    height: 38,
                    borderRadius: '8px',
                    fontSize: 13,
                    fontWeight: 500,
                    ...chaaviGoldAddImageButtonSx,
                  }}
                >
                  Add images
                  <input type="file" hidden multiple accept="image/*" onChange={handleAddStagedFiles} />
                </Button>
              </Box>

              {isEdit && imagesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} sx={{ color: IM.green }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 1.5 }}>
                  {existingImages.map((img) => (
                    <Box
                      key={img.imageId}
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: `0.5px solid ${IM.border}`,
                        bgcolor: IM.fieldBg,
                      }}
                    >
                      <Box
                        component="img"
                        src={normalizeImageUrl(img.imageUrl)}
                        alt={img.imageName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {img.isPrimary && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#fff',
                            bgcolor: IM.green,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: '4px',
                          }}
                        >
                          Primary
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                          p: 0.25,
                        }}
                      >
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => void handleSetPrimary(img.imageId)}
                          sx={{ color: '#fff', p: 0.25 }}
                          title={img.isPrimary ? 'Primary' : 'Set as primary'}
                        >
                          {img.isPrimary ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          type="button"
                          size="small"
                          onClick={() => setImageToDelete(img)}
                          sx={{ color: '#ffcdd2', p: 0.25 }}
                          title="Remove image"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}

                  {stagedUploads.map((s, index) => (
                    <Box
                      key={`staged-${index}-${s.file.name}`}
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: `0.5px dashed ${IM.green}`,
                        bgcolor: IM.greenMuted,
                      }}
                    >
                      <Box component="img" src={s.preview} alt={s.file.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          color: IM.green,
                          bgcolor: '#fff',
                          px: 0.5,
                          borderRadius: '4px',
                        }}
                      >
                        New
                      </Box>
                      <IconButton
                        type="button"
                        size="small"
                        onClick={() => removeStagedAt(index)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(255,255,255,0.9)',
                          width: 28,
                          height: 28,
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: IM.required }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {stagedFiles.length > 0 && (
                <Typography sx={{ fontSize: 11, color: IM.hint, mt: 1 }}>
                  {stagedFiles.length} new file{stagedFiles.length === 1 ? '' : 's'} will upload when you save.
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              px: 3.5,
              py: 2,
              pb: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              type="button"
              onClick={handleClose}
              disabled={loading}
              sx={{
                height: 38,
                px: 2.25,
                borderRadius: '8px',
                border: `0.5px solid ${IM.border}`,
                color: IM.cancelColor,
                textTransform: 'none',
                fontSize: 14,
                bgcolor: 'transparent',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="contained"
              disabled={loading || imageUploading}
              startIcon={loading || imageUploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 38,
                px: 2.75,
                borderRadius: '8px',
                bgcolor: IM.green,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': { bgcolor: IM.greenHover, boxShadow: 'none' },
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              {loading || imageUploading
                ? imageUploading
                  ? 'Uploading…'
                  : 'Saving…'
                : isEdit
                  ? 'Update inventory'
                  : 'Create inventory'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(imageToDelete)} onClose={() => setImageToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Remove image?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Delete “{imageToDelete?.imageName}”? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImageToDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => void handleConfirmDeleteImage()} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InventoryManagementForm;
